import { Box, Divider, useMediaQuery, useTheme } from '@mui/material';
import { OcInsightsInstruction } from '../../../api/generated';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SelfServiceInstructionProps } from './InstructionComponent';
import { normalizeCaptureStartAt, useOcInsightsEvents } from '../../../api/resources/useOcInsightsEvents';
import { AnalysisStatus } from './components/AnalysisStatus';
import { EWC_CLASSES } from '../../../Constants';
import { Option } from './components/ApprovalForm';
import i18n from '../../../i18n';
import { WrapperCardWithTitle } from '../../../layouts/WrapperCardWithTitle';
import { useTranslation } from 'react-i18next';
import { useSelfServiceImages } from '../../../api/queries/selfService/selfServiceImages';
import { getMaterialDecision, PredictionWithApproval } from './materialDecision';
import { getEffectiveStatus, getInstructionText, getLowerStatus, getStatusConfig } from './analysisStatus';
import { getIsReady } from './ocInsightsReadiness';
import { useOcInsightsSkipTimeouts } from './hooks/useOcInsightsSkipTimeouts';
import { OcInsightsImageSelectorDialog } from './components/OcInsightsImageSelectorDialog';
import { OcInsightsImage } from './components/OcInsightsImage';
import { OcInsightsPredictionArea } from './components/OcInsightsPredictionArea';

const DEFAULT_TIME_OUT = 60_000;

export default function OcInsightsComponent({
  instruction,
  onReadyStatusChange,
  onInstructionChange,
  jobsiteId,
  sessionId,
}: SelfServiceInstructionProps<OcInsightsInstruction> & { jobsiteId: string; sessionId: string }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedCaptureStartAt, setSelectedCaptureStartAt] = useState<string | null>(null);
  const [isImageSelectorOpen, setIsImageSelectorOpen] = useState(false);
  const [isNoImageCorrect, setIsNoImageCorrect] = useState(false);
  const [switchedImage, setSwitchedImage] = useState(false);

  const imageCandidates = useSelfServiceImages({ jobsiteId, sessionId });

  const [materialApproval, setMaterialApproval] = useState<PredictionWithApproval>({
    predictedValue: undefined,
    selectedValue: null,
    isApproved: null,
  });

  const imageTimeoutMs = instruction.parameters?.timeout?.imageTimeoutMs ?? DEFAULT_TIME_OUT;
  const materialPredictionTimeoutMs = instruction.parameters?.timeout?.materialPredictionTimeoutMs ?? DEFAULT_TIME_OUT;

  const selectedImageIndex = useMemo(() => {
    if (!selectedCaptureStartAt) return null;

    const index = imageCandidates.data?.findIndex(
      (candidate) => normalizeCaptureStartAt(candidate.captureStartAt) === selectedCaptureStartAt
    );

    return index === undefined || index < 0 ? null : index;
  }, [imageCandidates.data, selectedCaptureStartAt]);

  const {
    connectionStatus,
    imageStatus,
    recordingStatus,
    edgeSizeStatus,
    materialTypeStatus,
    soilClassificationStatus,
    image,
    materialType,
    edgeSize,
    soilClassification,
    observedCaptureCount,
  } = useOcInsightsEvents(
    jobsiteId,
    sessionId,
    selectedCaptureStartAt,
    selectedImageIndex,
    selectedImageIndex !== null && !isNoImageCorrect
  );

  const isPredictionPending = selectedImageIndex !== null && !isNoImageCorrect;

  const {
    isImageTimedOut,
    isMaterialPredictionTimedOut,
    reset: resetSkipTimeouts,
  } = useOcInsightsSkipTimeouts({
    imageTimeoutMs,
    materialPredictionTimeoutMs,
    isImageFetched: image.isFetched,
    isMaterialPredictionPending: isPredictionPending,
    isMaterialPredictionFetched: materialType.isFetched,
  });

  const materialOptions: Option[] = getMaterialOptions();
  const candidateCount = imageCandidates.data?.length ?? 0;

  const emitInstructionUpdate = useCallback(
    (captureStartAt: string | null, switched: boolean, noImage: boolean, approval: PredictionWithApproval) => {
      const imageCaptureStartAt = noImage ? null : captureStartAt;
      const { status: materialStatus, selectedMaterial } = getMaterialDecision(approval);
      const licensePlateCapturedAt =
        imageCaptureStartAt === null
          ? null
          : (imageCandidates.data?.find(
              (candidate) => normalizeCaptureStartAt(candidate.captureStartAt) === imageCaptureStartAt
            )?.licensePlateCapturedAt ?? null);

      onInstructionChange?.({
        ...instruction,
        parameters: {
          ...instruction.parameters,
          image: { captureStartAt: imageCaptureStartAt, switchedImage: switched },
          licensePlate: { capturedAt: licensePlateCapturedAt },
          material: { status: materialStatus, selectedMaterial },
        },
      });
    },
    [imageCandidates.data, instruction, onInstructionChange]
  );

  const handleImageSelect = useCallback(
    (captureStartAt: string, userTriggered = false) => {
      const normalizedCaptureStartAt = normalizeCaptureStartAt(captureStartAt);
      if (!normalizedCaptureStartAt) return;

      const imageIndex = imageCandidates.data?.findIndex(
        (candidate) => normalizeCaptureStartAt(candidate.captureStartAt) === normalizedCaptureStartAt
      );
      if (imageIndex === undefined || imageIndex < 0) return;

      const selectedImage = imageCandidates.data?.[imageIndex];
      if (!selectedImage) return;

      setIsImageSelectorOpen(false);
      onReadyStatusChange(false);

      const newSwitched = userTriggered ? true : switchedImage;
      setSwitchedImage(newSwitched);
      setSelectedCaptureStartAt(normalizedCaptureStartAt);
      setIsNoImageCorrect(false);
      resetSkipTimeouts();

      const resetApproval: PredictionWithApproval = {
        predictedValue: undefined,
        selectedValue: null,
        isApproved: null,
      };
      setMaterialApproval(resetApproval);

      emitInstructionUpdate(normalizedCaptureStartAt, newSwitched, false, resetApproval);
    },
    [imageCandidates.data, onReadyStatusChange, switchedImage, emitInstructionUpdate, resetSkipTimeouts]
  );

  const handleImageChangeRequest = () => {
    setSwitchedImage(true);
    setIsImageSelectorOpen(true);
  };

  const handleNoImageMatches = () => {
    setIsImageSelectorOpen(false);
    onReadyStatusChange(false);
    setIsNoImageCorrect(true);
    resetSkipTimeouts();
    const noImageApproval: PredictionWithApproval = { predictedValue: null, selectedValue: null, isApproved: false };
    setMaterialApproval(noImageApproval);
    emitInstructionUpdate(null, switchedImage, true, noImageApproval);
  };

  const handleMaterialApproval = (isApproved: boolean) => {
    const newApproval: PredictionWithApproval = {
      ...materialApproval,
      isApproved,
      selectedValue: isApproved ? null : materialApproval.selectedValue,
    };
    setMaterialApproval(newApproval);
    emitInstructionUpdate(selectedCaptureStartAt, switchedImage, isNoImageCorrect, newApproval);
  };

  const handleMaterialOptionSelect = (selectedValue: string) => {
    const newApproval: PredictionWithApproval = { ...materialApproval, selectedValue };
    if (isMaterialPredictionTimedOut) {
      newApproval.predictedValue = null;
    }
    setMaterialApproval(newApproval);
    emitInstructionUpdate(selectedCaptureStartAt, switchedImage, isNoImageCorrect, newApproval);
  };

  const captureStatus = getLowerStatus(recordingStatus, imageStatus);

  const effectiveEdgeSizeStatus = getEffectiveStatus(edgeSize.isFetched, edgeSizeStatus, isPredictionPending);
  const effectiveMaterialTypeStatus = getEffectiveStatus(
    materialType.isFetched,
    materialTypeStatus,
    isPredictionPending
  );
  const effectiveSoilClassificationStatus = getEffectiveStatus(
    soilClassification.isFetched,
    soilClassificationStatus,
    isPredictionPending
  );
  const analysisStatus = getLowerStatus(
    getLowerStatus(effectiveEdgeSizeStatus, effectiveMaterialTypeStatus),
    effectiveSoilClassificationStatus
  );
  const instructionText = getInstructionText(connectionStatus, recordingStatus, imageStatus, analysisStatus);
  const selectedMaterialOption = isNoImageCorrect
    ? (materialApproval.selectedValue ?? undefined)
    : (materialApproval.selectedValue ?? materialApproval.predictedValue ?? undefined);

  const chipConfigs = [
    {
      label: t('selfService.connection'),
      config: getStatusConfig(connectionStatus),
    },
    {
      label: t('selfService.recording'),
      config: getStatusConfig(captureStatus),
    },
    {
      label: t('selfService.prediction'),
      config: getStatusConfig(analysisStatus),
    },
  ];

  const isReady = useMemo(
    () =>
      getIsReady({
        selectedImageIndex,
        isImageTimedOut,
        isImageFetched: image.isFetched,
        isNoImageCorrect,
        materialApproval,
      }),
    [selectedImageIndex, isImageTimedOut, image.isFetched, isNoImageCorrect, materialApproval]
  );

  useEffect(() => {
    onReadyStatusChange(isReady);
  }, [onReadyStatusChange, isReady]);

  useEffect(() => {
    const latestCandidate = imageCandidates.data?.[candidateCount - 1];

    if (!selectedCaptureStartAt && latestCandidate) {
      handleImageSelect(latestCandidate.captureStartAt);
    }
  }, [handleImageSelect, imageCandidates.data, selectedCaptureStartAt, candidateCount]);

  useEffect(() => {
    if (!isNoImageCorrect && materialType.isFetched) {
      setMaterialApproval((prev) => ({
        ...prev,
        predictedValue: materialType.data?.classId ?? null,
      }));
    }
  }, [isNoImageCorrect, materialType.data?.classId, materialType.isFetched]);

  useEffect(() => {
    if (isImageTimedOut && selectedImageIndex === null && !isNoImageCorrect) {
      setIsNoImageCorrect(true);
      const noImageApproval: PredictionWithApproval = { predictedValue: null, selectedValue: null, isApproved: false };
      setMaterialApproval(noImageApproval);
      emitInstructionUpdate(null, false, true, noImageApproval);
    }
  }, [emitInstructionUpdate, isImageTimedOut, isNoImageCorrect, selectedImageIndex]);

  const processingCount = Math.max(0, observedCaptureCount - candidateCount);
  const showPredictionCard = selectedImageIndex !== null || isNoImageCorrect;
  const showMaterialApproval =
    isNoImageCorrect || materialApproval.predictedValue !== undefined || isMaterialPredictionTimedOut;
  const selectionOnly = isNoImageCorrect || isMaterialPredictionTimedOut;

  return (
    <Box flex={1} display='flex' flexDirection='column' gap={2}>
      <OcInsightsImageSelectorDialog
        isMobile={isMobile}
        open={isImageSelectorOpen}
        onClose={() => setIsImageSelectorOpen(false)}
        images={imageCandidates.data ?? []}
        selectedCaptureStartAt={selectedCaptureStartAt}
        processingCount={processingCount}
        onSelect={handleImageSelect}
        onSelectNone={handleNoImageMatches}
      />
      <WrapperCardWithTitle title='analysis.title' elevation={0} sx={{ height: 'auto', flex: '0 0 auto' }}>
        <AnalysisStatus chipConfigs={chipConfigs} instructionText={instructionText} />
      </WrapperCardWithTitle>
      <Divider />
      <OcInsightsImage
        selectedImageIndex={selectedImageIndex}
        isNoImageCorrect={isNoImageCorrect}
        isImageTimedOut={isImageTimedOut}
        image={image}
        processingCount={processingCount}
        onImageChangeRequest={handleImageChangeRequest}
      />
      {showPredictionCard && (
        <OcInsightsPredictionArea
          isNoImageCorrect={isNoImageCorrect}
          materialType={materialType}
          effectiveMaterialTypeStatus={effectiveMaterialTypeStatus}
          edgeSize={edgeSize}
          effectiveEdgeSizeStatus={effectiveEdgeSizeStatus}
          soilClassification={soilClassification}
          effectiveSoilClassificationStatus={effectiveSoilClassificationStatus}
          showMaterialApproval={showMaterialApproval}
          selectionOnly={selectionOnly}
          materialApproval={materialApproval}
          materialOptions={materialOptions}
          selectedMaterialOption={selectedMaterialOption}
          isMaterialPredictionTimedOut={isMaterialPredictionTimedOut}
          onMaterialApproval={handleMaterialApproval}
          onMaterialOptionSelect={handleMaterialOptionSelect}
        />
      )}
    </Box>
  );
}

function getMaterialOptions(): Option[] {
  return EWC_CLASSES.map((ewc) => ({
    value: `${ewc.class}_${ewc.label}`,
    label: `${ewc.class} — ${i18n.t(`material.class.${ewc.label}`)}`,
  }));
}
